import { createAppQueryBuilder } from '@autone/utils';
import { createApi } from '@reduxjs/toolkit/query/react';

import { customBaseQuery } from '../utils/custom-base-query';

import {
  type GetCoreAcademyRedirectUrlRequest,
  type GetCoreAcademyRedirectUrlResponse,
  type GetCoreDocumentationRedirectUrlRequest,
  type GetCoreDocumentationRedirectUrlResponse,
  type GetCoreProductPeekViewRequest,
  type GetCoreProductPeekViewResponse,
  type GetThoughtspotTokenQueryRequest,
  type GetThoughtspotTokenQueryResponse,
  type GetUserConfigRequest,
  type GetUserConfigResponse,
  type GetUsersRequest,
  type GetUsersResponse,
  type ValidateProductIdsRequest,
  type ValidateProductIdsResponse,
} from './core-others';

const CORE_API_PREFIX = '/core';

export const coreQueryBuilder = createAppQueryBuilder<
  'core',
  typeof CORE_API_PREFIX
>();

/**
 * @description core redux api to be used by other apps and packages
 * to be PREFERRED over using the coreApi exported from "@autone/ui"
 * to avoid circular dependencies.
 */
export const coreApiNext = createApi({
  reducerPath: 'coreApiNext',
  tagTypes: ['User'],
  baseQuery: async (args, api, extraOptions) => {
    return customBaseQuery(args, api, extraOptions, `v2${CORE_API_PREFIX}`);
  },
  endpoints: (builder) => ({
    getUserConfig: builder.query<GetUserConfigResponse, GetUserConfigRequest>({
      query: coreQueryBuilder('get', '/users/me'),
      providesTags: ['User'],
    }),
    getUsers: builder.query<GetUsersResponse, GetUsersRequest>({
      query: coreQueryBuilder('get', '/users'),
    }),
    getCoreProductPeekView: builder.query<
      GetCoreProductPeekViewResponse,
      GetCoreProductPeekViewRequest
    >({
      query: coreQueryBuilder('get', '/products/{product_id}/peak'),
    }),
    getCoreDocumentationRedirectUrl: builder.query<
      GetCoreDocumentationRedirectUrlResponse,
      GetCoreDocumentationRedirectUrlRequest
    >({
      query: coreQueryBuilder('get', '/auth/documentation-jwt'),
    }),
    getThoughtspotToken: builder.query<
      GetThoughtspotTokenQueryResponse,
      GetThoughtspotTokenQueryRequest
    >({
      query: coreQueryBuilder('post', '/auth/thoughtspot/token'),
    }),
    getCoreAcademyRedirectUri: builder.query<
      GetCoreAcademyRedirectUrlResponse,
      GetCoreAcademyRedirectUrlRequest
    >({
      query: coreQueryBuilder('get', '/auth/academy-redirect-uri'),
    }),
    validateProductIds: builder.query<
      ValidateProductIdsResponse,
      ValidateProductIdsRequest
    >({
      query: coreQueryBuilder('post', '/products/validate'),
    }),
  }),
});

export const {
  useGetUserConfigQuery,
  useGetUsersQuery,
  useGetCoreProductPeekViewQuery,
  useGetCoreDocumentationRedirectUrlQuery,
  useLazyGetCoreDocumentationRedirectUrlQuery,
  useGetThoughtspotTokenQuery,
  useLazyGetThoughtspotTokenQuery,
  useLazyGetCoreAcademyRedirectUriQuery,
  useValidateProductIdsQuery,
  useLazyValidateProductIdsQuery,
} = coreApiNext;
